import logging from '@/lib/logging';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { kebabCase } from 'lodash';
import React, { FC, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import styled from 'styled-components';

interface IconWrapperProps {
  size?: number;
}

const IconWrapper = styled.div<IconWrapperProps>`
  position: relative;
  ${customTextSize(0, 0)}

  width: ${(props) => (props.size ? remCalc(props.size) : '100%')};
  height: ${(props) => (props.size ? remCalc(props.size) : 'auto')};

  > div {
    width: 100%;
    height: 0;
    padding-bottom: 100%;
  }

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: auto;
  }
`;

interface importedGlyph {
  default: string;
  ReactComponent: React.FC<React.SVGProps<SVGSVGElement>>;
}

interface GlyphProps {
  name: string;
  size?: number;
}

const Glyph: FC<GlyphProps> = ({ name, size, ...props }) => {
  const ref = useRef<importedGlyph | null>(null);
  const [loading, setLoading] = useState(false);
  const [inViewRef, inView] = useInView({
    triggerOnce: true,
  });

  useEffect(() => {
    if (!inView) return;

    setLoading(true);

    import(`src/assets/images/svg/glyphs/${kebabCase(name)}.svg`)
      .then((icon: importedGlyph) => {
        ref.current = icon;
      })
      .catch((e) => {
        ref.current = null;
        logging.captureException(e);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [name, inView]);

  if (!loading && ref.current) {
    const { ReactComponent } = ref.current;

    return (
      <IconWrapper ref={inViewRef} size={size}>
        <div>
          <ReactComponent width={size} height={size} />
        </div>
      </IconWrapper>
    );
  }

  return (
    <IconWrapper ref={inViewRef} size={size}>
      <div />
    </IconWrapper>
  );
};

export default Glyph;
